import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import staffApi from "@/lib/staffApi";
import { PageHead } from "./DashboardLayout";
import { useStaff } from "@/context/StaffContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Plus, Trash2, Send, Truck } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function Settings() {
  const { staff, can } = useStaff();
  const [emails, setEmails] = useState([]);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [shipTesting, setShipTesting] = useState(false);
  const [frequency, setFrequency] = useState("daily");

  useEffect(() => {
    if (staff && !can("super")) return;
    staffApi.get("/admin/settings/digest-recipients")
      .then((r) => setEmails(r.data.emails || []))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    staffApi.get("/admin/settings/digest-frequency").then((r) => setFrequency(r.data.frequency || "daily")).catch(() => {});
  }, [staff, can]);

  if (staff && !can("super")) return <Navigate to="/staff/dashboard" replace />;

  const changeFrequency = async (freq) => {
    setFrequency(freq);
    try {
      await staffApi.put("/admin/settings/digest-frequency", { frequency: freq });
      toast.success(`Digest set to ${freq}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await staffApi.put("/admin/settings/digest-recipients", { emails: next });
      setEmails(data.emails || []);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const add = async (e) => {
    e.preventDefault();
    const email = input.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { toast.error("Enter a valid email address"); return; }
    if (emails.includes(email)) { toast("Already added"); setInput(""); return; }
    await persist([...emails, email]);
    setInput("");
    toast.success("Recipient added");
  };

  const remove = async (email) => {
    await persist(emails.filter((x) => x !== email));
    toast("Recipient removed");
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await staffApi.post("/admin/settings/digest-test");
      toast[data.status === "delivered" ? "success" : "error"](`Digest ${data.status} · ${data.sent_to}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send test digest");
    } finally { setTesting(false); }
  };

  const sendShipTest = async () => {
    setShipTesting(true);
    try {
      const { data } = await staffApi.post("/admin/settings/shipping-test");
      toast[data.status === "delivered" ? "success" : "error"](`Shipping email ${data.status} · ${data.sent_to}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send test shipping email");
    } finally { setShipTesting(false); }
  };

  return (
    <div data-testid="settings-page">
      <PageHead kicker="Configuration" title="Settings" />
      <div className="max-w-xl border border-white/10 bg-ink p-6">
        <div className="mb-1 flex items-center gap-2 text-cream"><Mail size={16} className="text-gold" /><h2 className="font-serif text-lg">Low-Stock Digest Recipients</h2></div>
        <p className="mb-5 text-sm text-cream/50">Every Super Admin and Admin already receives the digest. Add extra inboxes below (e.g. a warehouse address) to include them too.</p>

        <div className="mb-5 flex items-center justify-between border border-white/10 px-4 py-3" data-testid="digest-frequency">
          <div><p className="text-sm text-cream">Digest frequency</p><p className="text-xs text-cream/50">How often the low-stock digest is emailed</p></div>
          <div className="flex border border-white/15">
            {["daily", "weekly"].map((f) => (
              <button key={f} onClick={() => changeFrequency(f)} data-testid={`frequency-${f}`}
                className={`px-4 py-1.5 text-xs uppercase tracking-widest transition ${frequency === f ? "bg-gold text-black" : "text-cream/60 hover:text-gold"}`}>{f}</button>
            ))}
          </div>
        </div>

        <form onSubmit={add} className="mb-5 flex gap-2" data-testid="digest-add-form">
          <input value={input} onChange={(e) => setInput(e.target.value)} type="email" placeholder="warehouse@example.com"
            data-testid="digest-email-input" className="flex-1 border border-white/15 bg-black px-4 py-2.5 text-sm text-cream outline-none focus:border-gold" />
          <button disabled={saving} data-testid="digest-add-btn"
            className="flex items-center gap-2 bg-gold px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-black hover:bg-gold-bright disabled:opacity-50"><Plus size={14} /> Add</button>
        </form>

        {loading ? (
          <p className="text-sm text-cream/40">Loading…</p>
        ) : emails.length === 0 ? (
          <p className="border border-dashed border-white/10 px-4 py-6 text-center text-sm text-cream/40" data-testid="digest-empty">No extra recipients yet.</p>
        ) : (
          <ul className="divide-y divide-white/10 border border-white/10" data-testid="digest-list">
            {emails.map((email) => (
              <li key={email} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`digest-recipient-${email}`}>
                <span className="text-cream/80">{email}</span>
                <button onClick={() => remove(email)} disabled={saving} data-testid={`digest-remove-${email}`}
                  aria-label={`Remove ${email}`} className="text-cream/40 transition hover:text-red-400 disabled:opacity-50"><Trash2 size={15} /></button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-5">
          <p className="text-xs text-cream/50">Preview emails in your own inbox ({staff?.email}).</p>
          <div className="flex gap-2">
            <button onClick={sendShipTest} disabled={shipTesting} data-testid="shipping-test-btn"
              className="flex items-center gap-2 border border-white/20 px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-cream transition hover:border-gold hover:text-gold disabled:opacity-50">
              <Truck size={14} /> {shipTesting ? "Sending…" : "Test Shipping Email"}
            </button>
            <button onClick={sendTest} disabled={testing} data-testid="digest-test-btn"
              className="flex items-center gap-2 border border-gold px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-gold transition hover:bg-gold hover:text-black disabled:opacity-50">
              <Send size={14} /> {testing ? "Sending…" : "Send Test Digest"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
